import BaseService from './BaseService'

class PricingLogService extends BaseService {
  constructor() {
    super('/pricing-logs')
  }

  /**
   * Get paginated pricing logs with filtering
   * @param {Object} filters - Filter options
   * @param {number} filters.per_page - Items per page
   * @param {number} filters.page - Page number
   * @param {string} filters.operation - Filter by operation type
   * @param {string} filters.search - Search product name, user name, operation, or notes
   * @param {number} filters.product_id - Filter by product ID
   * @param {number} filters.user_id - Filter by user ID
   * @param {string} filters.start_date - Start date for filtering
   * @param {string} filters.end_date - End date for filtering
   * @param {string} filters.sort_by - Sort column
   * @param {string} filters.sort_order - Sort order (asc/desc)
   */
  async getPricingLogs(filters = {}) {
    const params = new URLSearchParams()

    // Pagination
    if (filters.per_page) params.append('per_page', filters.per_page)
    if (filters.page) params.append('page', filters.page)

    // Filtering
    if (filters.operation) params.append('operation', filters.operation)
    if (filters.search) params.append('search', filters.search)
    if (filters.product_id) params.append('product_id', filters.product_id)
    if (filters.user_id) params.append('user_id', filters.user_id)
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)

    // Sorting
    if (filters.sort_by) params.append('sort_by', filters.sort_by)
    if (filters.sort_order) params.append('sort_order', filters.sort_order)

    const queryString = params.toString()
    const endpoint = `${queryString ? `?${queryString}` : ''}`

    return this.customRequest('get', endpoint)
  }

  /**
   * Get a single pricing log by ID
   * @param {number} pricingLogId - The pricing log ID
   */
  async getPricingLog(pricingLogId) {
    return this.getById(pricingLogId)
  }

  /**
   * Create a new pricing log entry
   * @param {Object} data - Pricing log data
   * @param {number} data.product_id - Product ID
   * @param {number} data.old_cost - Previous cost
   * @param {number} data.new_cost - New cost
   * @param {number} data.old_price - Previous price
   * @param {number} data.new_price - New price
   * @param {number} data.quantity - Quantity associated with change
   * @param {string} data.operation - Operation type
   * @param {string} data.notes - Notes about the change
   * @param {Object} data.metadata - Additional metadata
   */
  async createPricingLog(data) {
    return this.create(data)
  }

  /**
   * Get pricing summary statistics
   * @param {Object} filters - Filter options for summary
   * @param {string} filters.search - Search product name, user name, operation, or notes
   * @param {number} filters.product_id - Filter by product ID
   * @param {string} filters.start_date - Start date for filtering
   * @param {string} filters.end_date - End date for filtering
   * @param {string} filters.operation - Filter by operation type
   */
  async getPricingSummary(filters = {}) {
    const params = new URLSearchParams()

    if (filters.product_id) params.append('product_id', filters.product_id)
    if (filters.search) params.append('search', filters.search)
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    if (filters.operation) params.append('operation', filters.operation)

    const queryString = params.toString()
    const endpoint = `/summary${queryString ? `?${queryString}` : ''}`

    return this.customRequest('get', endpoint)
  }

  /**
   * Get pricing logs for a specific product
   * @param {number} productId - Product ID
   * @param {Object} filters - Filter options
   * @param {number} filters.per_page - Items per page
   * @param {number} filters.page - Page number
   * @param {string} filters.operation - Filter by operation type
   * @param {string} filters.start_date - Start date for filtering
   * @param {string} filters.end_date - End date for filtering
   */
  async getPricingLogsForProduct(productId, filters = {}) {
    const params = new URLSearchParams()

    if (filters.per_page) params.append('per_page', filters.per_page)
    if (filters.page) params.append('page', filters.page)
    if (filters.operation) params.append('operation', filters.operation)
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)

    const queryString = params.toString()
    const endpoint = `/product/${productId}${queryString ? `?${queryString}` : ''}`

    return this.customRequest('get', endpoint)
  }

  /**
   * Get pricing trends for a product
   * @param {number} productId - Product ID
   * @param {Object} options - Trend analysis options
   * @param {number} options.days - Number of days to analyze (default: 30)
   * @param {string} options.group_by - Grouping method: 'day', 'week', 'month' (default: 'day')
   */
  async getPricingTrends(productId, options = {}) {
    const params = new URLSearchParams()

    if (options.days) params.append('days', options.days)
    if (options.group_by) params.append('group_by', options.group_by)

    const queryString = params.toString()
    const endpoint = `/product/${productId}/trends${queryString ? `?${queryString}` : ''}`

    return this.customRequest('get', endpoint)
  }

  /**
   * Get recent pricing activity
   * @param {number} limit - Number of recent logs to retrieve (default: 10)
   */
  async getRecentActivity(limit = 10) {
    return this.customRequest('get', `?per_page=${limit}&sort_by=created_at&sort_order=desc`)
  }

  /**
   * Get pricing logs with cost changes only
   * @param {Object} filters - Filter options
   */
  async getCostChanges(filters = {}) {
    return this.getPricingLogs({ ...filters, operation: 'cost_adjustment' })
  }

  /**
   * Get pricing logs with price changes only
   * @param {Object} filters - Filter options
   */
  async getPriceChanges(filters = {}) {
    return this.getPricingLogs({ ...filters, operation: 'price_adjustment' })
  }

  /**
   * Get pricing logs for purchases
   * @param {Object} filters - Filter options
   */
  async getPurchaseLogs(filters = {}) {
    return this.getPricingLogs({ ...filters, operation: 'purchase' })
  }

  /**
   * Get available operation types for filtering
   * @returns {Array} Array of operation types
   */
  getOperationTypes() {
    return [
      { value: null, label: 'All Operations' },
      { value: 'purchase', label: 'Purchase' },
      { value: 'price_adjustment', label: 'Price Adjustment' },
      { value: 'cost_adjustment', label: 'Cost Adjustment' },
      { value: 'initial', label: 'Initial' },
      { value: 'bulk_update', label: 'Bulk Update' },
      { value: 'vendor_change', label: 'Vendor Change' },
      { value: 'market_adjustment', label: 'Market Adjustment' }
    ]
  }

  /**
   * Export pricing logs (if backend supports it)
   * @param {Object} filters - Filter options
   * @param {string} format - Export format (csv, pdf, etc.)
   */
  async exportPricingLogs(filters = {}, format = 'csv') {
    const params = new URLSearchParams()

    // Add all filters
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null) {
        params.append(key, filters[key])
      }
    })

    params.append('format', format)

    const queryString = params.toString()
    const endpoint = `/export${queryString ? `?${queryString}` : ''}`

    return this.customRequest('get', endpoint, null, { responseType: 'blob' })
  }
}

export const pricingLogService = new PricingLogService()
